'use client'

import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { CircleNotchIcon } from '@phosphor-icons/react'
import { MultiSelectProperty, PROPERTY_ICONS } from '@/ui/property-fields'
import { MediaPreviewCard } from '../media-preview/card'
import {
  type LabelSuggestion,
  type MediaEditValues,
} from '../media-preview/types'
import type { MediaKind } from '@/entries'
import type { EnrichedMediaResult } from '@/enrichment'
import type { PersonWithLastInteraction } from '@/entries'

type Person = PersonWithLastInteraction

interface MediaFormProps {
  // Media detection and enrichment
  aiDetectedType: MediaKind | null
  enrichedData: EnrichedMediaResult | null
  isEnriching: boolean
  enrichmentError: string | null

  // Manual entry mode
  showManualForm: boolean
  dismissedMediaType: MediaKind | null
  editedMediaValues: MediaEditValues | undefined
  genresInputValue: string

  // People selection
  selectedPeople: PersonWithLastInteraction[]
  availablePeople: PersonWithLastInteraction[]
  isPeopleLoading: boolean

  // Label suggestions (for artists)
  labelSuggestions: LabelSuggestion[]
  isCheckingLabels: boolean

  // Loading state
  isSubmitting: boolean

  // Callbacks
  onConfirm: () => void
  onDismiss: () => void
  onRetry: () => void
  onSelectAlternative: (alternative: {
    externalId: string
    name: string
    imageUrl?: string
    type?: string
    genres?: string[]
    year?: number
  }) => void
  onAddPerson: (person: Person) => void
  onRemovePerson: (person: Person) => void
  onAddNewPerson: () => void
  onToggleLabelSuggestion: (externalId: string) => void
  onEditChange: (values: MediaEditValues | undefined) => void
  onGenresInputChange: (value: string) => void
  onManualSubmit: () => void
  onManualCancel: () => void
}

export function MediaForm({
  aiDetectedType,
  enrichedData,
  isEnriching,
  enrichmentError,
  showManualForm,
  dismissedMediaType,
  editedMediaValues,
  genresInputValue,
  selectedPeople,
  availablePeople,
  isPeopleLoading,
  labelSuggestions,
  isCheckingLabels,
  isSubmitting,
  onConfirm,
  onDismiss,
  onRetry,
  onSelectAlternative,
  onAddPerson,
  onRemovePerson,
  onAddNewPerson,
  onToggleLabelSuggestion,
  onEditChange,
  onGenresInputChange,
  onManualSubmit,
  onManualCancel,
}: MediaFormProps) {
  // Render enriched media preview
  if (aiDetectedType && !showManualForm) {
    return (
      <MediaPreviewCard
        type={(enrichedData?.type ?? aiDetectedType) as MediaKind}
        enrichedData={enrichedData}
        isEnriching={isEnriching}
        isSubmitting={isSubmitting}
        error={enrichmentError}
        onConfirm={onConfirm}
        onDismiss={onDismiss}
        onRetry={onRetry}
        onSelectAlternative={onSelectAlternative}
        selectedPeople={selectedPeople}
        availablePeople={availablePeople}
        onAddPerson={onAddPerson}
        onRemovePerson={onRemovePerson}
        onAddNewPerson={onAddNewPerson}
        isPeopleLoading={isPeopleLoading}
        labelSuggestions={labelSuggestions}
        onToggleLabelSuggestion={onToggleLabelSuggestion}
        isCheckingLabels={isCheckingLabels}
        onEditChange={onEditChange}
        editedValues={editedMediaValues}
        className="animate-section-enter"
      />
    )
  }

  // Render manual media entry form
  if (showManualForm && dismissedMediaType) {
    return (
      <div className="animate-section-enter space-y-3 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">
            Add{' '}
            {dismissedMediaType === 'artist' || dismissedMediaType === 'label'
              ? dismissedMediaType
              : dismissedMediaType === 'release'
                ? 'Release'
                : dismissedMediaType.charAt(0).toUpperCase() +
                  dismissedMediaType.slice(1)}{' '}
            manually
          </h3>
          <button
            type="button"
            onClick={onManualCancel}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            Cancel
          </button>
        </div>

        <div className="space-y-3">
          {/* Title/Name */}
          <div>
            <label className="text-muted-foreground mb-1 block text-xs">
              {dismissedMediaType === 'artist' || dismissedMediaType === 'label'
                ? 'Name'
                : 'Title'}
            </label>
            <Input
              value={editedMediaValues?.title || editedMediaValues?.name || ''}
              onChange={(e) => {
                const value = e.target.value
                onEditChange({
                  ...editedMediaValues,
                  ...(dismissedMediaType === 'artist' ||
                  dismissedMediaType === 'label'
                    ? { name: value }
                    : { title: value }),
                })
              }}
              className="h-8 text-sm"
              placeholder={
                dismissedMediaType === 'artist' ||
                dismissedMediaType === 'label'
                  ? 'Name'
                  : 'Title'
              }
            />
          </div>

          {/* Director (movies) */}
          {dismissedMediaType === 'movie' && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Director
              </label>
              <Input
                value={editedMediaValues?.director || ''}
                onChange={(e) => {
                  onEditChange({
                    ...editedMediaValues,
                    director: e.target.value || null,
                  })
                }}
                className="h-8 text-sm"
                placeholder="Director name"
              />
            </div>
          )}

          {/* Author (books) */}
          {dismissedMediaType === 'book' && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Author
              </label>
              <Input
                value={editedMediaValues?.author || ''}
                onChange={(e) => {
                  onEditChange({
                    ...editedMediaValues,
                    author: e.target.value || null,
                  })
                }}
                className="h-8 text-sm"
                placeholder="Author name"
              />
            </div>
          )}

          {/* Website (labels) */}
          {dismissedMediaType === 'label' && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Website
              </label>
              <Input
                value={editedMediaValues?.website || ''}
                onChange={(e) => {
                  onEditChange({
                    ...editedMediaValues,
                    website: e.target.value || null,
                  })
                }}
                className="h-8 text-sm"
                placeholder="https://example.com"
              />
            </div>
          )}

          {/* Year and BPM/Key (tracks) */}
          {(dismissedMediaType === 'movie' ||
            dismissedMediaType === 'book' ||
            dismissedMediaType === 'release') && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-muted-foreground mb-1 block text-xs">
                  Year
                </label>
                <Input
                  type="number"
                  value={editedMediaValues?.year?.toString() || ''}
                  onChange={(e) => {
                    onEditChange({
                      ...editedMediaValues,
                      year: e.target.value
                        ? parseInt(e.target.value, 10)
                        : null,
                    })
                  }}
                  className="h-8 text-sm"
                  placeholder="2024"
                />
              </div>
              {dismissedMediaType === 'release' && (
                <>
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs">
                      BPM
                    </label>
                    <Input
                      type="number"
                      value={editedMediaValues?.bpm?.toString() || ''}
                      onChange={(e) => {
                        onEditChange({
                          ...editedMediaValues,
                          bpm: e.target.value
                            ? parseInt(e.target.value, 10)
                            : null,
                        })
                      }}
                      className="h-8 text-sm"
                      placeholder="120"
                    />
                  </div>
                  <div>
                    <label className="text-muted-foreground mb-1 block text-xs">
                      Key
                    </label>
                    <Input
                      value={editedMediaValues?.key || ''}
                      onChange={(e) => {
                        onEditChange({
                          ...editedMediaValues,
                          key: e.target.value || null,
                        })
                      }}
                      className="h-8 text-sm"
                      placeholder="C major"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Year (artists, labels) */}
          {(dismissedMediaType === 'artist' ||
            dismissedMediaType === 'label') && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Year (optional)
              </label>
              <Input
                type="number"
                value={editedMediaValues?.year?.toString() || ''}
                onChange={(e) => {
                  onEditChange({
                    ...editedMediaValues,
                    year: e.target.value ? parseInt(e.target.value, 10) : null,
                  })
                }}
                className="h-8 text-sm"
                placeholder="2024"
              />
            </div>
          )}

          {/* Genres */}
          {(dismissedMediaType === 'artist' ||
            dismissedMediaType === 'label' ||
            dismissedMediaType === 'release' ||
            dismissedMediaType === 'movie') && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs">
                Genres (comma-separated)
              </label>
              <Input
                value={genresInputValue}
                onChange={(e) => {
                  const inputValue = e.target.value
                  // Store raw input value to allow typing commas
                  onGenresInputChange(inputValue)
                  // Parse and update editedMediaValues
                  if (inputValue.trim() === '') {
                    onEditChange({
                      ...(editedMediaValues || {}),
                      genres: undefined,
                    })
                  } else {
                    const genresArray = inputValue
                      .split(',')
                      .map((g) => g.trim())
                      .filter(Boolean)
                    onEditChange({
                      ...(editedMediaValues || {}),
                      genres: genresArray.length > 0 ? genresArray : undefined,
                    })
                  }
                }}
                onBlur={() => {
                  // On blur, clean up trailing commas/spaces
                  const cleaned = genresInputValue
                    .split(',')
                    .map((g) => g.trim())
                    .filter(Boolean)
                    .join(', ')
                  if (cleaned !== genresInputValue) {
                    onGenresInputChange(cleaned)
                  }
                }}
                className="h-8 text-sm"
                placeholder="Rock, Pop, Jazz"
              />
            </div>
          )}

          {/* Description/Bio/Profile */}
          <div>
            <label className="text-muted-foreground mb-1 block text-xs">
              {dismissedMediaType === 'artist'
                ? 'Bio'
                : dismissedMediaType === 'label'
                  ? 'Profile'
                  : 'Description'}
            </label>
            <Textarea
              value={
                editedMediaValues?.description ||
                editedMediaValues?.bio ||
                editedMediaValues?.profile ||
                ''
              }
              onChange={(e) => {
                const value = e.target.value
                onEditChange({
                  ...editedMediaValues,
                  ...(dismissedMediaType === 'artist'
                    ? { bio: value || undefined }
                    : dismissedMediaType === 'label'
                      ? { profile: value || undefined }
                      : { description: value || undefined }),
                })
              }}
              className="min-h-[80px] resize-none text-sm"
              placeholder={
                dismissedMediaType === 'artist'
                  ? 'Artist biography...'
                  : dismissedMediaType === 'label'
                    ? 'Label description...'
                    : 'Description...'
              }
            />
          </div>

          {/* People selector */}
          <div className="flex items-center gap-2 border-t pt-3">
            <MultiSelectProperty
              label="With"
              Icon={PROPERTY_ICONS.people}
              selectedItems={selectedPeople as Person[]}
              availableItems={availablePeople as Person[]}
              onAdd={onAddPerson}
              onRemove={onRemovePerson}
              getItemLabel={(p) => p.title}
              disabled={isSubmitting}
              isLoading={isPeopleLoading}
              onAddNew={onAddNewPerson}
              addNewLabel="Add new..."
            />
          </div>

          {/* Submit button */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onManualCancel}
              disabled={isSubmitting}
              className="h-8 px-3 text-xs"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={onManualSubmit}
              disabled={
                isSubmitting ||
                !(editedMediaValues?.title || editedMediaValues?.name)
              }
              className="h-8 px-3 text-xs"
            >
              {isSubmitting ? (
                <>
                  <CircleNotchIcon className="mr-1 h-3.5 w-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add to Library'
              )}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
